import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RoutesPath } from '../routes-path';
import { User } from '../models/user/user.model';
import { UserSettings } from '@shared/models/user/user-settings.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { SocialAuthService } from '@abacritt/angularx-social-login';

@Injectable()
export class StorageService {
  static SESSION_COOKIE: string = 'ph-jwt-token';
  static WAS_VISITED: string = '';
  static SESSION_REFRESH_COOKIE: string = 'ph-refresh-jwt-token';
  static USER_COOKIE: string = 'ph-user';
  static MOBILE_APP_BANNER_COOKIE = 'ph-mobile-app-banner';
  static USER_SETTINGS: string = 'ph-user-settings';
  static AUTH_FROM_HELP_UKRAINE: string = 'ph-auth-from-help-ukraine';
  static AUTH_FROM_WCD: string = 'ph-auth-from-wcd';
  static AUTH_FROM_BACKBASE: string = 'ph-auth-from_backbase';
  public storageSub: any = new Subject<any>();
  public noScrollAfterChangeRoute: boolean = false;

  public _userInfo$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  _userInfo = this._userInfo$.asObservable();

  constructor(
    public router: Router,
    public translate: TranslateService,
    public socialAuthService: SocialAuthService
  ) {}

  changeUserInfo(ui: boolean) {
    this._userInfo$.next(ui);
  }

  get(key: string) {
    return localStorage.getItem(key);
  }

  getLang() {
    return this.get('lang');
  }

  put(key: string, val: any) {
    if (key === 'autofocus') {
      this.storageSub.next('added');
    }
    localStorage.setItem(key, val);
  }

  delete(key: string) {
    this.storageSub.next('removed');
    localStorage.removeItem(key);
  }

  logout(goToHome: boolean = true) {
    this.delete(StorageService.SESSION_COOKIE);
    this.delete(StorageService.SESSION_REFRESH_COOKIE);
    this.delete(StorageService.USER_COOKIE);
    this.delete(StorageService.USER_SETTINGS);
    this.delete(StorageService.AUTH_FROM_HELP_UKRAINE);
    this.delete(StorageService.AUTH_FROM_WCD);
    this.delete(StorageService.MOBILE_APP_BANNER_COOKIE);
    this.socialAuthService
      .signOut()
      .then()
      .catch(() => {});

    this.changeUserInfo(false);

    if (goToHome) {
      this.router.navigate(['/' + RoutesPath.default.home]);
    }
  }

  get user(): User {
    return JSON.parse(this.get(StorageService.USER_COOKIE));
  }

  get userSettings(): UserSettings {
    return JSON.parse(this.get(StorageService.USER_SETTINGS));
  }

  get isAuthFromUkraine(): boolean {
    return JSON.parse(this.get(StorageService.AUTH_FROM_HELP_UKRAINE));
  }

  get isAuthFromWCD(): boolean {
    return JSON.parse(this.get(StorageService.AUTH_FROM_WCD));
  }

  get isAuthFromBackbase(): boolean {
    return JSON.parse(this.get(StorageService.AUTH_FROM_BACKBASE));
  }

  setToken(token) {
    this.put(StorageService.SESSION_COOKIE, token.token);
    this.put(StorageService.SESSION_REFRESH_COOKIE, token.refresh_token);
  }

  setUser(user) {
    this.put(StorageService.USER_COOKIE, JSON.stringify(user));
  }

  setAuthFromUkraine(auth: boolean) {
    this.put(StorageService.AUTH_FROM_HELP_UKRAINE, auth);
  }

  setAuthFromWcd(auth: boolean) {
    this.put(StorageService.AUTH_FROM_WCD, auth);
  }

  setAuthFromBackbase(auth: boolean) {
    this.put(StorageService.AUTH_FROM_BACKBASE, auth);
  }

  setUserSettings(userSettings) {
    this.put(StorageService.USER_SETTINGS, JSON.stringify(userSettings));
  }

  hideMobileAppBanner() {
    this.put(StorageService.MOBILE_APP_BANNER_COOKIE, 'hidden');
  }

  isMobileAppBannerHidden(): boolean {
    return this.get(StorageService.MOBILE_APP_BANNER_COOKIE) == 'hidden';
  }

  projectOwnership(id): boolean {
    if (this.user) {
      return id === this.user.id;
    }
    return false;
  }

  eventOwnership(id): boolean {
    if (this.user) {
      return id === this.user.id;
    }
    return false;
  }

  setVisit() {
    this.put(StorageService.WAS_VISITED, 'true');
  }

  setAvatarInitials(name: string, surname: string) {
    if (name === null && surname === null) {
      return this.translate.instant('GLOBAL.anonymous');
    }
    return (name ? name.charAt(0) : '') + ' ' + (surname ? surname.charAt(0) : '');
  }

  formatVote(vote: number) {
    if (Number.isInteger(vote)) {
      return vote.toFixed(1);
    }
    return vote;
  }
}
